import { useQuery } from '@tanstack/react-query';
import {
  type NotificationFilter,
  NotificationType,
  type UseQueryReturnType,
  gqlClient,
  graphql,
  queryClient,
} from '@tyro/api';
import { shellKeys } from '../keys';

const notifications = graphql(/* GraphQL */ `
  query communications_notifications($filter: NotificationFilter) {
    communications_notifications(filter: $filter) {
      id
      title
      text
      notificationType
      sentOn
      sender {
        partyId
        title {
          id
          name
          nameTextId
        }
        firstName
        lastName
        avatarUrl
        type
      }
      recipients {
        recipientPartyId
        readOn
        status
        metaData {
          partyId
          feeId
          mailId
          threadMessageId
          notificationType
          optionId
          studentPartyId
          substitutionDate
          schoolActivityId
          assessmentId
          formId
        }
      }
    }
  }
`);

const notificationsQuery = (filter: NotificationFilter) => ({
  queryKey: shellKeys.notifications.list(filter),
  queryFn: async () => {
    const { communications_notifications } = await gqlClient.request(
      notifications,
      { filter },
    );

    return {
      communications_notifications: communications_notifications.map(
        (notification) => {
          let notificationId = '';

          switch (notification?.notificationType) {
            case NotificationType.Mail:
              notificationId = [
                notification.id,
                notification.recipients?.[0]?.metaData?.threadMessageId,
              ]
                .filter(Boolean)
                .join('-');
              break;
            default:
              notificationId = `${notification.id}`;
              break;
          }

          return {
            ...notification,
            notificationId,
          };
        },
      ),
    };
  },
});

export function useNotifications(filter: NotificationFilter) {
  return useQuery({
    ...notificationsQuery(filter),
    select: ({ communications_notifications }) => communications_notifications,
  });
}

export function getNotifications(filter: NotificationFilter) {
  return queryClient.fetchQuery(notificationsQuery(filter));
}

export type ReturnTypeFromNotifications = UseQueryReturnType<
  typeof useNotifications
>[number];
